import { useMemo, useState, useCallback } from "react";
import {
  isCollapsed,
  getNextIndex,
  getPreviousIndex,
  isPointAtWordEnd,
  getNodeDeserializer,
} from "@udecode/slate-plugins";
import { Transforms, Range } from "slate";
import { useTranslation } from "react-i18next";

import { isWordAfterTrigger } from "../../editor-utils";
import { insertVariable } from "./transforms/insert-variable";

const testatorInitialValue = {
  name: "",
  last_name: "",
  email: "",
  properties: {
    curp: "",
    maritalStatus: "",
    address: {
      zipCode: "",
      street: "",
      exteriorNumber: "",
      interiorNumber: "",
      state: "",
      municipio: "",
    },
  },
};

const executorInitalValue = testatorInitialValue;

export const heirInitialValue = {
  name: "",
  last_name: "",
  email: "",
  properties: {
    percentageOfInheritanceState: "",
  },
};

const legateeInitialValue = {
  name: "",
  last_name: "",
  email: "",
  properties: {
    curp: "",
    legacyDescription: "",
  },
};

export const witnessInitialValue = {
  name: "",
  last_name: "",
  email: "",
  properties: {
    curp: "",
  },
};

export const testamentInitialValues = {
  testator: testatorInitialValue,
  heirs: [heirInitialValue],
  legatee: legateeInitialValue,
  executor: executorInitalValue,
  witnesses: [witnessInitialValue, witnessInitialValue, witnessInitialValue],
};

function addTestamentProperties(participant, constants, { t }) {
  if (["testator", "executor"].includes(participant.testamentRole)) {
    constants.push({
      name: t("contracts_.constants.properties.maritalStatus"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.maritalStatus",
    });
    constants.push({
      name: t("contracts_.constants.properties.address.zipCode"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.address.zipCode",
    });
    constants.push({
      name: t("contracts_.constants.properties.address.street"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.address.street",
    });
    constants.push({
      name: t("contracts_.constants.properties.address.externalNumber"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.address.externalNumber",
    });
    constants.push({
      name: t("contracts_.constants.properties.address.internalNumber"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.address.internalNumber",
    });
    constants.push({
      name: t("contracts_.constants.properties.address.state"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.address.state",
    });
    constants.push({
      name: t("contracts_.constants.properties.address.municipio"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.address.municipio",
    });
  }
  if (["testator", "executor", "legatee"].includes(participant.testamentRole)) {
    constants.push({
      name: t("CURP"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.curp",
    });
  }
  if (["legatee"].includes(participant.testamentRole)) {
    constants.push({
      name: t("contracts_.constants.properties.legacyDescription"),
      value: "",
      type: "constant",
      _id: participant._id,
      apiName: "properties.legacyDescription",
    });
  }
  // constants.push({ name: t('Porcentaje de la masa hereditaria'), value: '', type: 'constant', _id: participant._id, apiName: 'properties.percentageOfInheritanceState' })
}

export const useVariables = (
  participants = [],
  {
    maxSuggestions = 5,
    trigger = "@",
    jurisdiction,
    isTestament,
    ...options
  } = {}
) => {
  const { t } = useTranslation();
  const [targetRange, setTargetRange] = useState(null);
  const [valuePath, setValuePath] = useState([0, 0]);
  const [search, setSearch] = useState("");
  const values = useMemo(() => {
    const searchTerm = search.toLowerCase().replace(/_/g, " ");
    const participantVariables = participants.reduce(
      (options, participant, i) => {
        let constants = [
          {
            name: t("contracts_.constants.signedAt"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "signedAt",
          },
          {
            name: t("contracts_.constants.obligation"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "obligation",
          },
          {
            name: t("contracts_.constants.label"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "label",
          },
          {
            name: t("contracts_.constants.email"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "email",
          },
        ];

        if (jurisdiction === "pr") {
          constants.push({
            name: "DNI/C.E./otro",
            value: "",
            type: "constant",
            id: participant._id,
            apiName: "cpf",
          });
          constants.push({
            name: "Cargo",
            value: "",
            type: "constant",
            id: participant._id,
            apiName: "cargo",
          });
        }
        if (jurisdiction === "co") {
          constants.push({
            name: "Cédula no.",
            value: "",
            type: "constant",
            id: participant._id,
            apiName: "cpf",
          });
        }
        if (jurisdiction === "br") {
          constants.push({
            name: "Cargo",
            value: "",
            type: "constant",
            id: participant._id,
            apiName: "cargo",
          });
          constants.push({
            name: "RG",
            value: "",
            type: "constant",
            id: participant._id,
            apiName: "rg",
          });
          constants.push({
            name: "CPF",
            value: "",
            type: "constant",
            id: participant._id,
            apiName: "cpf",
          });
        }

        if (participant.obligation === "legal") {
          constants.push({
            name: t("contracts_.constants.company_name"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "name",
          });
          constants.push({
            name: t("contracts_.constants.representative"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "representative",
          });
        } else {
          constants.push({
            name: t("contracts_.constants.name"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "name",
          });
          constants.push({
            name: t("contracts_.constants.last_name"),
            value: "",
            type: "constant",
            _id: participant._id,
            apiName: "last_name",
          });
        }

        if (isTestament) {
          addTestamentProperties(participant, constants, { t });
        }
        constants = constants.filter(
          (c) =>
            c.name.toLowerCase().includes(searchTerm) ||
            searchTerm.startsWith("pro")
        );

        const variables = participant.variables
          .map((variable) => {
            return {
              name: variable.name,
              _id: variable._id,
              type: variable.type,
              value: variable.value,
              apiName: variable.apiName,
            };
          })
          .filter(
            (v) => v.name.toLowerCase().includes(searchTerm) && v.apiName.length
          );

        const matched = variables.some(
          (v) => v.name.toLowerCase() === searchTerm
        );

        options.push({
          entity: "participant",
          label: participant.label,
          participantId: participant._id,
          participantIndex: i + 1,
          matched,
          options: [...variables, ...constants].slice(0, maxSuggestions),
        });
        return options;
      },
      []
    );

    const contractProperties = {
      entity: "contract",
      label: "ContractProperties",
      options: [
        {
          name: t("editor.variables.popup_properties_docname"),
          value: "",
          type: "constant",
          _id: "contract",
          apiName: "name",
        },
        {
          name: t("editor.variables.popup_properties_docid"),
          value: "",
          type: "constant",
          _id: "contract",
          apiName: "documentId",
        },
        {
          name: t("editor.variables.popup_properties_signdate"),
          value: "",
          type: "constant",
          _id: "contract",
          apiName: "signedAt",
        },
      ],
    };

    if (isTestament) {
      contractProperties.options.push({
        name: t("contracts_.constants.testamentHeirsNames"),
        value: "",
        type: "constant",
        _id: "contract",
        apiName: "testamentHeirsNames",
      });
      contractProperties.options.push({
        name: t("contracts_.constants.testamentWitnessesNames"),
        value: "",
        type: "constant",
        _id: "contract",
        apiName: "testamentWitnessesNames",
      });
    } else {
      contractProperties.options.push({
        name: t("editor.variables.popup_properties_externalId"),
        value: "",
        type: "constant",
        _id: "contract",
        apiName: "externalId",
      });
    }

    return [...participantVariables, contractProperties];
  }, [participants, t, search, maxSuggestions]);

  const onAddVariable = useCallback(
    (editor, data) => {
      if (targetRange !== null) {
        Transforms.select(editor, targetRange);
        insertVariable(editor, data, options);
        return setTargetRange(null);
      }
    },
    [options, targetRange]
  );

  const onKeyDownVariable = useCallback(
    (e, editor) => {
      const [entityIndex, optionIndex] = valuePath;
      const entity = values[entityIndex];
      const entityOptions = entity.options;
      if (targetRange) {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          return setValuePath(
            getNextIndex(optionIndex, entityOptions.length - 1)
          );
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          return setValuePath(
            getPreviousIndex(optionIndex, entityOptions.length - 1)
          );
        }
        if (e.key === "Escape") {
          e.preventDefault();
          return setTargetRange(null);
        }

        if (["Tab", "Enter"].includes(e.key)) {
          e.preventDefault();
          onAddVariable(editor, entityOptions[optionIndex]);
          return false;
        }
      }
    },
    [
      values,
      valuePath,
      setValuePath,
      targetRange,
      setTargetRange,
      onAddVariable,
    ]
  );

  const onChangeVariable = useCallback(
    (editor) => {
      const { selection } = editor;

      if (selection && isCollapsed(selection)) {
        const cursor = Range.start(selection);

        const { range, match: beforeMatch } = isWordAfterTrigger(editor, {
          at: cursor,
          trigger,
          spaceChar: "_",
        });

        if (beforeMatch && isPointAtWordEnd(editor, { at: cursor })) {
          setTargetRange(range);
          const [, word] = beforeMatch;
          setSearch(word);
          setValuePath([0, 0]);
          return;
        }
      }

      setTargetRange(null);
    },
    [setTargetRange, setSearch, setValuePath, trigger]
  );

  return {
    search,
    path: valuePath,
    target: targetRange,
    values,
    onChangeVariable,
    onKeyDownVariable,
    onAddVariable,
  };
};
